import type { Gait } from "../loco/settings.js";
import type { FlyInput, WalkInput } from "./walk.js";

/**
 * Raw movement input shared by the keyboard, the touch joystick and mouse look. Mutable on purpose:
 * `useFrame` reads it every frame without going through React. Reset when a mode ends or the tab
 * loses focus, so a key released off-screen can never leave the player running.
 */
export class MoveInput {
  readonly keys = new Set<string>();
  /** Touch joystick, each -1..1 (x right, y forward). */
  joyX = 0;
  joyY = 0;
  /** Accumulated look since the last frame (radians). */
  lookYaw = 0;
  lookPitch = 0;
  /** On-screen turn buttons: -1 left, 1 right (radians per second are applied by the rig). */
  turn = 0;
  /** Edge-triggered: a jump was requested since the last frame. */
  jumpQueued = false;
  /** Aim held (right mouse button, a gamepad's left trigger): the body faces the camera. */
  aim = false;
  /** Gamepad sticks this frame, each -1..1 (written by the rig's poll). */
  padX = 0;
  padY = 0;
  padSprint = false;

  reset(): void {
    this.keys.clear();
    this.joyX = 0;
    this.joyY = 0;
    this.turn = 0;
    this.lookYaw = 0;
    this.lookPitch = 0;
    this.jumpQueued = false;
    this.aim = false;
    this.padX = 0;
    this.padY = 0;
    this.padSprint = false;
  }

  private axis(plus: readonly string[], minus: readonly string[]): number {
    let v = 0;
    if (plus.some((k) => this.keys.has(k))) v += 1;
    if (minus.some((k) => this.keys.has(k))) v -= 1;
    return v;
  }

  get run(): boolean {
    return this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
  }

  walk(): WalkInput {
    const jump = this.jumpQueued || this.keys.has("Space");
    this.jumpQueued = false;
    return {
      forward: clamp(this.axis(["KeyW", "ArrowUp"], ["KeyS", "ArrowDown"]) + this.joyY),
      right: clamp(this.axis(["KeyD", "ArrowRight"], ["KeyA", "ArrowLeft"]) + this.joyX),
      run: this.run || Math.hypot(this.joyX, this.joyY) > 0.92,
      jump,
    };
  }

  /** Alt or Caps Lock: walk instead of the default jog. */
  get walkHeld(): boolean {
    return this.keys.has("AltLeft") || this.keys.has("AltRight") || this.keys.has("CapsLock");
  }

  /**
   * The character's input (GTA style): the keyboard jogs, Shift sprints, Alt or Caps Lock walks; a
   * stick or the touch joystick moves as far as it is pushed (jog speed at the rim, a full push on
   * touch or the stick click sprints). The jump is an edge, consumed here.
   */
  loco(): { moveX: number; moveZ: number; gait: Gait; jump: boolean } {
    const jump = this.jumpQueued;
    this.jumpQueued = false;
    const kx = this.axis(["KeyD", "ArrowRight"], ["KeyA", "ArrowLeft"]);
    const kz = this.axis(["KeyW", "ArrowUp"], ["KeyS", "ArrowDown"]);
    let moveX = kx + this.joyX + this.padX;
    let moveZ = kz + this.joyY + this.padY;
    const len = Math.hypot(moveX, moveZ);
    // Keys are digital: a diagonal is a full-speed diagonal, not 1.41.
    if (len > 1) {
      moveX /= len;
      moveZ /= len;
    }
    const sprint = this.run || this.padSprint || Math.hypot(this.joyX, this.joyY) > 0.92;
    const gait: Gait = this.walkHeld ? "walk" : sprint ? "sprint" : "jog";
    return { moveX, moveZ, gait, jump };
  }

  fly(): FlyInput {
    return {
      forward: clamp(this.axis(["KeyW", "ArrowUp"], ["KeyS", "ArrowDown"]) + this.joyY),
      right: clamp(this.axis(["KeyD", "ArrowRight"], ["KeyA", "ArrowLeft"]) + this.joyX),
      up: this.axis(["Space"], ["KeyC", "ControlLeft"]),
      fast: this.run,
    };
  }

  /** Mouse or touch look, returned once and cleared. */
  takeLook(): [number, number] {
    const out: [number, number] = [this.lookYaw, this.lookPitch];
    this.lookYaw = 0;
    this.lookPitch = 0;
    return out;
  }
}

const clamp = (v: number): number => Math.max(-1, Math.min(1, v));

/** Radians of look per pixel of mouse or touch movement. */
export const LOOK_SENSITIVITY = 0.0024;

export const moveInput = new MoveInput();

/** Keys that move the player; the page must not scroll on them. */
export const MOVE_KEYS: ReadonlySet<string> = new Set([
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Space",
  "ShiftLeft",
  "ShiftRight",
  "AltLeft",
  "AltRight",
  "CapsLock",
  "KeyC",
  "ControlLeft",
]);

/** True when the key event came from a text field (typing in chat must never move the player). */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}
